import { getDatasets } from 'api/dataset/dataset.api'
import { Bid, BidTypeName, PlanValues } from 'api/elplan/elplan.types'
import { roundToDayAheadBidValueDecimals } from 'helpers/elplan.helper/bidValue.helper'
import { convertNumberDecimalSign } from 'helpers/global.helper/global.helper'
import i18n from 'i18next'
import uiConfigStore from 'store/uiConfig/uiConfig'
import Datetime from 'utils/datetime/datetime'
import { snapshot } from 'valtio'
import type { FormattedBid, FormattedBidWithIdAndTitle } from 'views/DayAheadTradingView/DayAheadTradingView.types'

export type BidValue = {
  time: ISODateTime
  volume: number
}

export type BidValues = { time: ISODateTime | number; volume?: number }[]

function getBidVolumeWithConvertedDecimalSign(bid: FormattedBid): number | number[] | undefined {
  if (Array.isArray(bid.volume)) {
    return bid.volume.map((volume) => convertNumberDecimalSign(volume))
  }
  if (typeof bid.volume === 'number') {
    return convertNumberDecimalSign(bid.volume)
  }
  return bid.volume
}

export function buildDayAheadBidCreatePayload(bid: FormattedBid, elplanInfoId: number, partitionId?: number) {
  return {
    elplan_info: elplanInfoId,
    tag: bid.tag,
    start_time: Datetime.toISOString(bid.startTime),
    end_time: Datetime.toISOString(bid.endTime),
    type: bid.type,
    volume: getBidVolumeWithConvertedDecimalSign(bid),
    price: bid.price ? convertNumberDecimalSign(bid.price) : undefined,
    name: bid.name || '-',
    electricity_price_scenario: bid.scenarioId,
    ...(partitionId === undefined ? {} : { partition: partitionId }),
    generate_block_order: true,
  }
}

export function buildDayAheadBidUpdatePayload(bid: FormattedBidWithIdAndTitle) {
  return {
    id: bid.id,
    tag: bid.tag,
    start_time: Datetime.toISOString(bid.startTime),
    end_time: Datetime.toISOString(bid.endTime),
    volume: getBidVolumeWithConvertedDecimalSign(bid),
    price: bid.price ? convertNumberDecimalSign(bid.price) : undefined,
    name: bid.name,
  }
}

export function buildDayAheadBidMutation(bid: FormattedBidWithIdAndTitle, elplanInfoId: number, partitionId?: number) {
  if (bid.fake) {
    return {
      operation: 'create' as const,
      payload: buildDayAheadBidCreatePayload(bid, elplanInfoId, bid.partitionId ?? partitionId),
    }
  }

  return {
    operation: 'update' as const,
    payload: buildDayAheadBidUpdatePayload(bid),
  }
}

export function buildDayAheadBidCopyPayload(
  elplanInfoId: number,
  fromDate: ISODateTime,
  toDate: ISODateTime,
  bidType: BidTypeName,
  partitionId?: number
) {
  return {
    elplan_info: elplanInfoId,
    from_date: fromDate,
    to_date: toDate,
    type: bidType,
    ...(partitionId === undefined ? {} : { partition: partitionId }),
  }
}

export function formatBid(bid: Bid): FormattedBidWithIdAndTitle {
  return {
    id: bid.id,
    fake: false,
    title:
      bid.type === 'blockbid'
        ? `${i18n.t('Block bid')} ${bid.props.block_order}`
        : bid.type === 'spotbid_without_price' || bid.type === 'spotbid_with_price'
          ? i18n.t('Spot bid')
          : i18n.t('Profile bid'),
    type: bid.type,
    tag: bid.tag,
    date: Datetime.toLocalTime(bid.start_time, 'onlyDate'),
    startTime: bid.start_time,
    endTime: bid.end_time,
    displayStartTime: Datetime.toLocalTime(bid.start_time, 'hour'),
    displayEndTime: Datetime.toLocalTime(Datetime.ISODatetimeTo59Minutes(bid.end_time), 'hour'),
    volume: bid.volume ?? undefined,
    price: bid.props.price,
    block_order: bid.type === 'blockbid' || bid.type === 'profilebid' ? bid.props.block_order : undefined,
    editable: !bid.is_locked,
    clear: true,
    name: bid.name,
    scenarioId: bid.electricity_price_scenario,
    partitionId: bid.partition ?? undefined,
  }
}

export function getElplanAsBids(dataset: Dataset, onlyPositiveBids?: boolean): BidValues {
  let volume = undefined
  const bids = dataset.times.map((time, index) => {
    if (dataset.values[index] === null || dataset.values[index] === undefined) {
      volume = undefined
    } else if (onlyPositiveBids && dataset.values[index] < 0) {
      volume = 0
    } else {
      const convertedDecimalSignNumber = convertNumberDecimalSign(dataset.values[index] ?? 0)
      volume =
        convertedDecimalSignNumber !== undefined
          ? roundToDayAheadBidValueDecimals(convertedDecimalSignNumber)
          : undefined
    }
    return { time, volume }
  })

  return bids
}

export function getDefaultBlockBid(topicalPeriod: {
  startTime: ISODateTime
  endTime: ISODateTime
}): FormattedBidWithIdAndTitle {
  return {
    id: 1,
    type: 'blockbid',
    fake: true,
    title: i18n.t('Block bid'),
    date: Datetime.toLocalTime(topicalPeriod?.startTime, 'onlyDate'),
    startTime: topicalPeriod?.startTime,
    endTime: topicalPeriod?.endTime,
    displayStartTime: Datetime.toLocalTime(topicalPeriod?.startTime, 'hour'),
    displayEndTime: Datetime.toLocalTime(Datetime.ISODatetimeTo59Minutes(topicalPeriod?.endTime), 'hour'),
    volume: 0,
    price: 0,
    editable: true,
    clear: true,
    name: ``,
  }
}

export async function getSpotBidsFromOptElplan(
  startTime: ISODateTime,
  endTime: ISODateTime,
  onlyPositiveBids?: boolean
): Promise<BidValues> {
  const EL_PLAN_SOURCE = 'net_exports.el.el_balance'
  const uiConfigSnap = snapshot(uiConfigStore)
  const uiConfig = uiConfigSnap.getAnchorUiConfigByType('base_config')

  if (!uiConfig) {
    throw new Error(`No UiConfig for "base_config" found to retreive electricity plan.`)
  }

  if (!uiConfig.id) {
    throw new Error(`No id found in UiConfig for "base_config" to retreive electricity plan.`)
  }

  const datasets = await getDatasets(
    uiConfig.id,
    uiConfig.version,
    uiConfig?.dataset_instructions,
    startTime,
    endTime,
    {
      onlyReturnIds: [EL_PLAN_SOURCE],
      overrideAlias: {
        opt_job_type_id: 2,
      },
    }
  )
  const optPlanDataset = datasets.find(({ return_id }) => return_id === EL_PLAN_SOURCE)

  if (!optPlanDataset) {
    throw new Error(`No Dataset "${EL_PLAN_SOURCE}" found in UiConfig for "base_config"`)
  }

  const bids = getElplanAsBids(optPlanDataset, onlyPositiveBids)
  return bids
}

export function generatePlanValues(volume: number, startTime: string, endTime: string): PlanValues[] {
  const planValues: PlanValues[] = []
  const start = Datetime.toISOString(startTime)
  const end = Datetime.toISOString(endTime)
  const time = Datetime.getNrOfHoursBetween(start, end)
  const volumePerHour = volume / time

  for (let i = 0; i < time; i++) {
    const startHour = Datetime.addHours(start, i)
    const endHour = Datetime.ISODatetimeTo59Minutes(Datetime.addHours(start, i))
    planValues.push({
      start_time: startHour,
      end_time: endHour,
      volume: volumePerHour,
    })
  }

  return planValues
}

export function splitHourlyBidsInto15MinIntervals(hourlyBids: BidValues): BidValue[] {
  return hourlyBids.flatMap((bid) => {
    const bidDateTime = new Datetime(bid.time as ISODateTime)
    const volume = bid.volume !== undefined && !isNaN(parseFloat(bid.volume.toString())) ? Number(bid.volume) : 0
    if (bidDateTime.date.getUTCMinutes() === 0) {
      // Split into 4 quarters for whole hour
      return [0, 15, 30, 45].map((min) => {
        const quarterDate = new Datetime(bidDateTime.ISOString).addMinutes(min)
        return {
          time: quarterDate.ISOString as ISODateTime,
          volume: volume,
        }
      })
    }
    // Not a whole hour, keep as is
    return [
      {
        time: bid.time as ISODateTime,
        volume: volume,
      },
    ]
  })
}
